import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Handle,
  Position,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { X } from 'lucide-react';

import {
  coerceValue,
  computeReorderTarget,
  cycleMark,
  findCellUnderPointer,
  insertValueAt,
  moveValue,
  nextPointerName,
  reindexEdgesForInsert,
  reindexEdgesForMove,
  reindexEdgesForRemove,
  relinkPointer,
  removePointer,
  removeValueAt,
  renamePointer,
} from './visualizationArrayGraphOps';

import type { ArrayView, StackView, CellMark, IndexPointer } from '@/features/Visualizer/types';
import { TONES } from '@/features/Visualizer/theme';

interface Props {
  kind: 'array' | 'stack';
  value: ArrayView | StackView;
  onChange: (next: ArrayView | StackView) => void;
  /** True while the enclosing block card (in `VisualizationCanvasStage.tsx`) is being resized — the mouse passes over this whole row during that drag, which would otherwise trigger every insert-gap "+" via `group-hover` at once, purely as a side effect of cursor position rather than genuine interest in inserting a value. Suppressing the `group` class for the duration is the simplest way to disable that reveal without new CSS. */
  suppressHoverAffordances?: boolean;
}

interface CellNodeData {
  index: number;
  displayValue: string;
  mark?: CellMark;
  showConnector: boolean;
  onValueChange: (index: number, raw: string) => void;
  onCycleMark: (index: number) => void;
  onRemove: (index: number) => void;
}

const CELL_WIDTH = 88;
const CELL_HEIGHT = 48;
const CELL_GAP = 32;
const ROW_Y = 40;
// Shared with `contentWidth` below and the node-creation sites that build `GapNode`/`PointerNode`
// instances — kept as named constants so a size/offset change at a creation site can't silently
// drift out of sync with the width math (that drift is exactly how the row-width-clipping bug this
// file previously fixed got introduced in the first place).
const GAP_WIDTH = 16;
const POINTER_OFFSET = 10;
const POINTER_WIDTH = 44;

/** One array/stack value. Text wraps up to 3 lines instead of truncating — real trainer content is often phrase-length, not a single digit. */
function CellNode({ data }: NodeProps) {
  const { index, displayValue, mark, showConnector, onValueChange, onCycleMark, onRemove } = data as unknown as CellNodeData;
  // Local editing buffer, seeded from and re-synced to `displayValue` whenever
  // it changes externally (e.g. an insert/remove elsewhere reindexes this
  // cell). Typing itself updates `text` synchronously so the textarea always
  // reflects exactly what was typed, rather than being purely controlled by
  // `displayValue` — a value that only advances once the caller's `onChange`
  // round-trips back through a new `value` prop, which real callers do, but
  // which is one render later than the keystroke itself.
  const [text, setText] = useState(displayValue);
  useEffect(() => setText(displayValue), [displayValue]);
  return (
    <div
      className="relative flex min-h-[48px] w-[88px] flex-col items-center justify-center rounded-md border-2 bg-white p-1"
      style={mark ? { backgroundColor: `${TONES[mark.tone]}22`, borderColor: TONES[mark.tone] } : { borderColor: '#d1d5db' }}
    >
      {showConnector && <Handle type="target" position={Position.Left} id="cell-target" />}
      {showConnector && <Handle type="source" position={Position.Right} id="cell-source" />}
      <button
        type="button"
        aria-label="Cycle cell mark colour"
        onClick={() => onCycleMark(index)}
        className="nodrag absolute -right-1 -top-1 h-3 w-3 rounded-full border border-white"
        style={{ backgroundColor: mark ? TONES[mark.tone] : '#d1d5db' }}
      />
      <textarea
        rows={3}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onValueChange(index, e.target.value);
        }}
        className="nodrag line-clamp-3 w-full resize-none border-none bg-transparent text-center text-xs outline-none"
      />
      <div className="mt-1 flex items-center gap-1 text-[10px] text-[#9ca3af]">
        <span>{index}</span>
        <button
          type="button"
          aria-label={`Remove value at index ${index}`}
          onClick={() => onRemove(index)}
          className="nodrag text-destructive hover:underline"
        >
          <X className="h-2.5 w-2.5" />
        </button>
      </div>
    </div>
  );
}

interface GapNodeData {
  atIndex: number;
  onInsert: (index: number) => void;
}

/** Hover-revealed "+" occupying the space between two cells (or before the first / after the last) — the one surviving "+ " affordance, since it targets an exact position rather than always appending. */
function GapNode({ data }: NodeProps) {
  const { atIndex, onInsert } = data as unknown as GapNodeData;
  return (
    <button
      type="button"
      aria-label={`Insert a value before index ${atIndex}`}
      onClick={() => onInsert(atIndex)}
      className="nodrag flex h-4 w-4 items-center justify-center rounded-full border border-dashed border-primary/60 bg-white text-[10px] text-primary opacity-0 hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-60"
    >
      +
    </button>
  );
}

interface PointerNodeData {
  name: string;
  onRename: (oldName: string, newName: string) => void;
  onRemove: (name: string) => void;
}

/** Free-floating pointer chip — no connector handle (Task 5 makes the whole node draggable-to-connect instead), matching the whole-node-drag gesture the trainer asked for. */
function PointerNode({ data }: NodeProps) {
  const { name, onRename, onRemove } = data as unknown as PointerNodeData;
  // Same local-buffer-plus-resync pattern as CellNode's `text` state: the caller's `onRename` only
  // becomes reflected in `name` once its `onChange` round-trips a new `pointers` array back through
  // props, which is one render later than the keystroke — buffering locally keeps every keystroke visible.
  const [text, setText] = useState(name);
  useEffect(() => setText(name), [name]);
  return (
    <div className="flex items-center gap-1 rounded-full bg-[#1c1917] px-1.5 py-0.5 text-[10px] font-semibold text-white">
      <input
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onRename(name, e.target.value);
        }}
        className="nodrag w-8 border-none bg-transparent text-white outline-none"
      />
      <button type="button" aria-label={`Remove pointer ${name}`} onClick={() => onRemove(name)} className="nodrag">
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}

const nodeTypes = { arrayCellNode: CellNode, arrayGapNode: GapNode, arrayPointerNode: PointerNode };

// Cell 0 starts at `CELL_GAP` rather than `0`, deliberately leaving room before it for the
// leading "insert before index 0" gap node to sit at a non-negative x. Without this offset the
// leading gap would need a negative x to sit before cell 0, which places it outside (to the left
// of) this canvas's own React Flow pane origin — clipped and unclickable in a real browser even
// though jsdom's layoutless tests can't detect it.
function layoutCells(count: number): number[] {
  const xs: number[] = [];
  for (let i = 0; i < count; i++) xs.push(CELL_GAP + i * (CELL_WIDTH + CELL_GAP));
  return xs;
}

// Stable fallbacks for optional array fields — `value.values ?? []` looks
// harmless, but a fresh `[]` literal is a NEW reference on every render
// whenever the field is absent, which breaks every `useMemo`/`useCallback`
// downstream that lists it as a dep (they see a "changed" input on every
// render even though nothing changed) and, combined with the below data-only
// resync effect (whose own dependency is that same unstable chain), created
// a real infinite render loop rather than just wasted recomputation.
const EMPTY_VALUES: (string | number)[] = [];
const EMPTY_MARKS: CellMark[] = [];
const EMPTY_POINTERS: IndexPointer[] = [];

function Canvas({ kind, value, onChange, suppressHoverAffordances }: Props) {
  const values = value.values ?? EMPTY_VALUES;
  const marks = value.marks ?? EMPTY_MARKS;
  const markByIndex = useMemo(() => new Map(marks.map((m) => [m.index, m])), [marks]);

  const onValueChange = useCallback(
    (index: number, raw: string) => {
      const nextValues = [...values];
      nextValues[index] = coerceValue(raw);
      onChange({ ...value, values: nextValues });
    },
    [values, value, onChange]
  );

  const onCycleMark = useCallback(
    (index: number) => onChange({ ...value, marks: cycleMark(marks, index) }),
    [marks, value, onChange]
  );

  // Purely ephemeral, session-local edges the trainer draws between two cells to annotate a
  // relationship (e.g. "these two swap") — never derived from or written back into `value`/`onChange`,
  // so they vanish on reload rather than becoming part of the persisted array data. Declared here
  // (rather than nearer the ReactFlow JSX below) so `onRemove`/`onInsert` right below can reindex
  // them alongside the structural mutation that produced them.
  const [edges, setEdges] = useEdgesState<Edge>([]);

  // Edges reference cells positionally via `cell-<index>` ids; a remove/insert that doesn't also
  // reindex or drop the edges referencing shifted/removed cells would leave React Flow silently
  // still rendering an edge between whatever cells now happen to occupy those ids — the exact
  // "stale edge reappears connecting two unrelated values" bug the design spec's edge-lifecycle
  // requirement exists to prevent.
  const onRemove = useCallback(
    (index: number) => {
      onChange(removeValueAt(value, index));
      setEdges((eds) => reindexEdgesForRemove(eds, index));
    },
    [value, onChange, setEdges]
  );
  const onInsert = useCallback(
    (index: number) => {
      onChange(insertValueAt(value, index));
      setEdges((eds) => reindexEdgesForInsert(eds, index));
    },
    [value, onChange, setEdges]
  );

  const pointers: IndexPointer[] = kind === 'array' ? (value as ArrayView).pointers ?? EMPTY_POINTERS : EMPTY_POINTERS;

  const onRenamePointer = useCallback(
    (oldName: string, newName: string) => onChange({ ...value, pointers: renamePointer(pointers, oldName, newName) } as ArrayView),
    [pointers, value, onChange]
  );
  const onRemovePointer = useCallback(
    (name: string) => onChange({ ...value, pointers: removePointer(pointers, name) } as ArrayView),
    [pointers, value, onChange]
  );

  const cellXs = layoutCells(values.length);

  // The block's outer card (`ViewBlockNode` in VisualizationCanvasStage.tsx) is `w-fit` — it
  // sizes itself to this component's content. But our cells/pointers/gaps are absolutely
  // positioned by React Flow, so they contribute nothing to this div's intrinsic width the way
  // normal flowing content would; left to the ambient 100%-of-parent default, the whole chain
  // collapses to some small minimum and every cell past index 0 is clipped. Compute the actual
  // rightmost extent of everything we lay out (cells, the trailing insert-gap, and any pointer
  // chip past the last cell) and give the wrapper that as an explicit width.
  const contentWidth = useMemo(() => {
    const CONTAINER_PADDING = 24;
    // `cellXs` already starts cell 0 at `CELL_GAP` (not `0`) to leave room for the leading
    // insert-gap on the left, so every `maxRight` below — being derived from `cellXs` — already
    // carries that same leftward offset through to the right edge; no separate adjustment needed.
    if (values.length === 0) return CELL_WIDTH + CELL_GAP * 2 + CONTAINER_PADDING; // room for the lone insert affordance
    let maxRight = cellXs[values.length - 1] + CELL_WIDTH;
    if (kind === 'array') maxRight = Math.max(maxRight, cellXs[values.length - 1] + CELL_WIDTH + GAP_WIDTH); // trailing gap node
    for (const p of pointers) {
      const x = p.index >= values.length ? cellXs[values.length - 1] + CELL_WIDTH : cellXs[p.index];
      maxRight = Math.max(maxRight, x + POINTER_OFFSET + POINTER_WIDTH); // pointer chip offset + its own width
    }
    return maxRight + CONTAINER_PADDING;
  }, [cellXs, values.length, kind, pointers]);

  const nodes: Node[] = useMemo(() => {
    const cellNodes: Node[] = values.map((v, index) => ({
      id: `cell-${index}`,
      type: 'arrayCellNode',
      position: { x: cellXs[index], y: ROW_Y },
      // React Flow hides a node (visibility: hidden) until it has measured
      // dimensions via ResizeObserver; jsdom's ResizeObserver stub never
      // fires, so an explicit width/height keeps nodes visible/interactive
      // under Vitest without changing anything about real-browser layout.
      width: CELL_WIDTH,
      height: CELL_HEIGHT,
      draggable: true,
      data: {
        index,
        displayValue: String(v),
        mark: markByIndex.get(index),
        showConnector: kind === 'array',
        onValueChange,
        onCycleMark,
        onRemove,
      } satisfies CellNodeData,
    }));

    const gapNodes: Node[] = [];
    if (kind === 'array') {
      for (let i = 0; i <= values.length; i++) {
        // `i === 0` sits at `0`, inside the leading `CELL_GAP` margin `layoutCells` now reserves
        // before cell 0 — a non-negative x that stays within this canvas's own pane, unlike the
        // previous `-CELL_GAP` which placed it outside the pane's visible/clipped area.
        const x = i === 0 ? 0 : i === values.length ? cellXs[i - 1] + CELL_WIDTH : cellXs[i] - CELL_GAP / 2 - 8;
        gapNodes.push({
          id: `gap-${i}`,
          type: 'arrayGapNode',
          position: { x, y: ROW_Y + CELL_HEIGHT / 2 - 8 },
          width: GAP_WIDTH,
          height: GAP_WIDTH,
          draggable: false,
          data: { atIndex: i, onInsert } satisfies GapNodeData,
        });
      }
    }

    const pointerNodes: Node[] = pointers.map((p, i) => {
      // Off-end pointer (e.g. a loop that has consumed every element — see IndexPointer's doc
      // comment) has p.index === values.length, past the last valid cellXs entry; position it
      // past the last cell instead of falling back to index 0, mirroring GapNode's own
      // trailing-position calculation above.
      const x =
        p.index >= values.length
          ? (values.length === 0 ? 0 : cellXs[values.length - 1] + CELL_WIDTH)
          : cellXs[p.index];
      return {
        id: `pointer-${i}`,
        type: 'arrayPointerNode',
        position: { x: x + POINTER_OFFSET, y: ROW_Y - 30 },
        width: POINTER_WIDTH,
        height: 20,
        draggable: true,
        data: { name: p.name, onRename: onRenamePointer, onRemove: onRemovePointer } satisfies PointerNodeData,
      };
    });

    return [...cellNodes, ...gapNodes, ...pointerNodes];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, kind, markByIndex, onValueChange, onCycleMark, onRemove, onInsert, pointers, onRenamePointer, onRemovePointer]);

  const [flowNodes, setNodes] = useNodesState(nodes);

  // Rebuild (positions included) whenever the derived `nodes` array changes shape/content —
  // mirrors the outer stage's fingerprint-resync pattern (VisualizationCanvasStage.tsx).
  // Must be a `useEffect`, not `useMemo` — `useMemo` is for computing a value during
  // render; calling `setNodes` (a state setter, a side effect) from inside one triggers
  // React's "Cannot update state while rendering" violation. `useEffect` runs after
  // render, which is where a state-syncing side effect like this belongs.
  const fingerprint = nodes.map((n) => n.id).join('|') + JSON.stringify(values) + JSON.stringify(marks) + JSON.stringify(pointers);
  useEffect(() => setNodes(nodes), [fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Resync every node's `data` (never `position`) on EVERY `nodes` recompute, not just
   * shape/content changes. `CellNode`/`GapNode`/`PointerNode` are React Flow custom nodes —
   * React Flow only re-renders one when its own `data` object changes — and every one of
   * their callbacks (`onValueChange`, `onInsert`, `onRenamePointer`, …) closes over THIS
   * component's `value`/`onChange` props at the render that created them. When a SIBLING
   * view block on the outer stage changes (e.g. another block gets duplicated or edited),
   * `VisualizationCanvasStage` recreates every block's `onChange` prop with a new identity
   * even though THIS block's own `values`/`marks`/`pointers` content is unchanged — so the
   * `fingerprint` above (deliberately content-only, to avoid fighting an in-progress drag)
   * never fires, and without this second effect `flowNodes` would keep serving cells a
   * stale callback bound to a since-superseded `current.views` snapshot from
   * `VisualizationStageEditor` — silently dropping whatever that stale closure's own
   * update computes against a since-changed sibling views array. Data-only, so it can
   * safely run on every render without resetting a cell mid-drag the way rebuilding
   * position (the effect above) would.
   */
  useEffect(() => {
    setNodes((current) => {
      const dataById = new Map(nodes.map((n) => [n.id, n.data]));
      return current.map((n) => {
        const data = dataById.get(n.id);
        return data ? { ...n, data } : n;
      });
    });
  }, [nodes]); // eslint-disable-line react-hooks/exhaustive-deps

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((current) => applyNodeChanges(changes, current)),
    [setNodes]
  );

  const onConnect = useCallback((connection: Connection) => setEdges((eds) => addEdge(connection, eds)), [setEdges]);
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges]
  );

  const onNodeDragStop = useCallback(
    (_event: unknown, node: Node) => {
      if (node.id.startsWith('cell-')) {
        const draggedIndex = Number(node.id.slice('cell-'.length));
        const siblings = flowNodes
          .filter((n): n is Node => n.id.startsWith('cell-') && n.id !== node.id)
          .map((n) => ({ index: Number(n.id.slice('cell-'.length)), x: n.position.x, width: n.measured?.width ?? CELL_WIDTH }));
        const draggedCenterX = node.position.x + (node.measured?.width ?? CELL_WIDTH) / 2;
        const target = computeReorderTarget(siblings, draggedIndex, draggedCenterX);
        if (target !== draggedIndex) {
          onChange(moveValue(value, draggedIndex, target));
          setEdges((eds) => reindexEdgesForMove(eds, draggedIndex, target));
        } else {
          setNodes(nodes); // snap back to the tidy layout even when the order didn't change
        }
        return;
      }
      if (node.id.startsWith('pointer-')) {
        const pointerArrayIndex = Number(node.id.slice('pointer-'.length));
        const name = pointers[pointerArrayIndex]?.name;
        if (name === undefined) return; // stale node reference (pointer removed mid-drag) — nothing to relink
        const pointerRect = {
          x: node.position.x,
          y: node.position.y,
          width: node.measured?.width ?? 40,
          height: node.measured?.height ?? 24,
        };
        const cellRects = flowNodes
          .filter((n) => n.id.startsWith('cell-'))
          .map((n) => ({
            index: Number(n.id.slice('cell-'.length)),
            x: n.position.x,
            y: n.position.y,
            width: n.measured?.width ?? CELL_WIDTH,
            height: n.measured?.height ?? CELL_HEIGHT,
          }));
        const targetIndex = findCellUnderPointer(cellRects, pointerRect);
        if (targetIndex !== null) onChange({ ...value, pointers: relinkPointer(pointers, name, targetIndex) } as ArrayView);
        else setNodes(nodes); // dropped over empty space — snap back rather than silently unlinking
      }
    },
    [flowNodes, value, onChange, pointers, nodes, setNodes, setEdges]
  );

  const addPointer = useCallback(
    (index: number) => {
      if (kind !== 'array') return;
      const newPointer = { name: nextPointerName(pointers), index };
      onChange({ ...value, pointers: [...pointers, newPointer] } as ArrayView);
    },
    [kind, pointers, value, onChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      const dragged = e.dataTransfer.getData('application/x-kodprep-array-element');
      if (dragged !== 'pointer') return;
      e.preventDefault();
      const target = document.elementFromPoint(e.clientX, e.clientY);
      const cellEl = target?.closest<HTMLElement>('.react-flow__node[data-id^="cell-"]');
      if (!cellEl) return;
      addPointer(Number(cellEl.dataset.id!.slice('cell-'.length)));
    },
    [addPointer]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer.types?.includes('application/x-kodprep-array-element')) e.preventDefault();
  }, []);

  return (
    <div
      style={{ height: 140, width: contentWidth, minWidth: contentWidth }}
      // `group` makes GapNode's `group-hover:opacity-60` actually activate — without an ancestor
      // carrying this class, that Tailwind modifier never fires, and `hover:opacity-100` alone only
      // triggers once the mouse is already over the (invisible at opacity-0) 16x16px button itself,
      // an undiscoverable target. With `group` here, hovering anywhere over the row's content
      // reveals every gap faintly, and hovering the exact gap resolves it to full opacity.
      className={`${suppressHoverAffordances ? '' : 'group'} rounded-md border border-[#ececec]`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      // Same fix as VisualizationTreeGraphCanvas.tsx: stop a click on this nested canvas's own
      // cells/pointers/gaps from bubbling up and also selecting the outer block card, which
      // would otherwise make a Backspace meant for this canvas delete the whole block instead.
      onClick={(e) => e.stopPropagation()}
    >
      <ReactFlow
        nodes={flowNodes}
        edges={edges}
        nodeTypes={nodeTypes}
        nodesConnectable
        panOnDrag={false}
        zoomOnScroll={false}
        onNodesChange={onNodesChange}
        onNodeDragStop={onNodeDragStop}
        onConnect={onConnect}
        onEdgesChange={onEdgesChange}
      >
        <Background />
      </ReactFlow>
    </div>
  );
}

export function VisualizationArrayGraphCanvas(props: Props) {
  return (
    <ReactFlowProvider>
      <Canvas {...props} />
    </ReactFlowProvider>
  );
}
